import {Component, effect, input, Input, model, OnInit, output, signal} from '@angular/core';
import {MediaSource, MediaSourceType} from '../../../models/media-source';
import {RestApiService} from '../../../services/api/rest-api.service';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {NgIf} from '@angular/common';
import {ButtonDirective} from 'primeng/button';
import {Ripple} from 'primeng/ripple';
import {Tooltip} from 'primeng/tooltip';
import {MediaManagerComponent} from '../media-manager/media-manager.component';
import {DialogService} from 'primeng/dynamicdialog';
import {MediaFormComponent} from '../media-form/media-form.component';
import {readFileAsBase64} from '../../../utils/files-utils';
import {ConfirmationService} from 'primeng/api';
import {Translations} from '../../../services/translations.service';
import {TranslateService} from '@ngx-translate/core';

@UntilDestroy()
@Component({
  standalone: true,
  selector: 'app-media-selector',
  imports: [
    NgIf,
    ButtonDirective,
    Ripple,
    Tooltip
  ],
  templateUrl: './media-selector.component.html',
  styleUrl: './media-selector.component.scss'
})
export class MediaSelectorComponent implements OnInit {
  mediaSourceId = model(0);
  @Input() canvasWidth = '200px';
  @Input() canvasHeight = '300px';
  @Input() disabled = false;
  category = input('default');

  mediaSource: MediaSource = null!;
  loading = false;
  dragging = false;

  @Input()
  canBeRemoved: boolean = true;

  onRemove = output();

  constructor(private api: RestApiService,
              private confirmationService: ConfirmationService,
              private dialogService: DialogService,
              private translations: Translations,
              private translateService: TranslateService) {
    effect(async () => {
      if (this.mediaSourceId() > 0) {
        await this.loadData();
      }
    });
  }

  ngOnInit(): void {
  }

  private async loadData() {
    if (this.mediaSourceId() > 0) {
      try {
        this.loading = true;
        const response = await this.api.mediaSources.getById(this.mediaSourceId());
        this.mediaSource = response.data;
      } finally {
        this.loading = false;
      }
    }
  }

  onDragOver(event: DragEvent) {
    if (this.disabled) {
      return;
    }
    event.preventDefault();
    this.dragging = true;
  }

  onDragLeave(event: DragEvent) {
    if (this.disabled) {
      return;
    }
    event.preventDefault();
    this.dragging = false;
  }

  async onDrop(event: DragEvent) {
    if (this.disabled) {
      return;
    }
    event.preventDefault();
    this.dragging = false;

    const dialog = MediaManagerComponent.openDialog(this.dialogService, {
      uploadingFiles: event.dataTransfer?.files,
      category: this.category()
    });

    dialog.onClose
      .pipe(untilDestroyed(this))
      .subscribe((mediaSource: MediaSource) => {
        if (mediaSource) {
          this.mediaSource = mediaSource;
          this.mediaSourceId.set(mediaSource.mediaSourceId);
        }
      });
  }

  openSelectionDialog() {
    if (this.disabled) {
      return;
    }
    const dialog = MediaManagerComponent.openDialog(this.dialogService, {
      mediaId: this.mediaSourceId() ?? 0,
      category: this.category()
    });
    dialog.onClose
      .pipe(untilDestroyed(this))
      .subscribe((mediaSource: MediaSource) => {
        if (mediaSource) {
          this.mediaSource = mediaSource;
          this.mediaSourceId.set(mediaSource.mediaSourceId);
        }
      });
  }

  removeMediaAction($event: MouseEvent) {
    if (this.disabled) {
      return;
    }
    $event.stopPropagation();
    // show dialog asking for confirmation
    const ref = this.confirmationService.confirm({
      header: this.translateService.instant(this.translations.mediaSources.removeTitle),
      message: this.translateService.instant(this.translations.mediaSources.removeMessage),
      acceptLabel: this.translateService.instant(this.translations.mediaSources.removeConfirm),
      rejectLabel: this.translateService.instant(this.translations.commons.cancel),
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-secondary p-button-text',
      acceptIcon: 'pi pi-trash',
      rejectIcon: 'pi pi-times',

      accept: async () => {
        this.clearMedia();
      }
    });
  }

  clearMedia() {
    this.mediaSource = null!;
    this.mediaSourceId.set(0);
    this.onRemove.emit();
  }
}
